import React from "react";
import { Helmet } from "react-helmet";

const SITE_NAME = "Update Email Marketing Tools";
const SITE_URL = "https://updateemailmarketingtoolsdemo.web.app";
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_DESCRIPTION =
  "AI-powered email marketing platform with real-time open tracking, click tracking, SMTP management, and campaign automation. Built for marketers who care about real engagement.";
const DEFAULT_KEYWORDS =
  "email marketing, email tracking, open tracking, click tracking, SMTP, automation, AI email, campaign analytics, email marketing platform, email automation";

// Per-route meta config. Keys are pathnames (or path prefixes).
const ROUTE_META = {
  "/": {
    title: "Dashboard — Update Email Marketing Tools",
    description:
      "Manage email campaigns, track opens and clicks, and monitor analytics from one AI-powered dashboard.",
  },
  "/login": {
    title: "Login — Update Email Marketing Tools",
    description:
      "Sign in to Update Email Marketing Tools — AI-powered email tracking platform.",
  },
  "/reset-password": {
    title: "Reset Password — Update Email Marketing Tools",
    description: "Reset your Update Email Marketing Tools account password.",
  },
  "/admin": {
    title: "Admin Dashboard — Update Email Marketing Tools",
    description:
      "Manage your email marketing campaigns, contacts, and analytics from the admin dashboard.",
  },
  "/admin/custom-smtp": {
    title: "Custom SMTP Settings — Update Email Marketing Tools",
    description:
      "Configure custom SMTP servers for sending email campaigns through your own providers.",
  },
  "/admin/generate-email": {
    title: "AI Email Generator — Update Email Marketing Tools",
    description:
      "Generate AI-powered email content for your marketing campaigns.",
  },
  "/admin/cron-setting": {
    title: "Cron Settings — Update Email Marketing Tools",
    description: "Configure scheduled email sending with cron jobs.",
  },
  "/admin/email-templates": {
    title: "Email Templates — Update Email Marketing Tools",
    description: "Create and manage reusable email templates for your campaigns.",
  },
  "/admin/email-marketing": {
    title: "Email Marketing — Update Email Marketing Tools",
    description:
      "Send and manage email marketing campaigns with real-time tracking.",
  },
  "/admin/user-email-marketing": {
    title: "Contact Management — Update Email Marketing Tools",
    description: "Manage your email contacts and recipient lists.",
  },
  "/admin/user-marketing-smpt": {
    title: "User SMTP Settings — Update Email Marketing Tools",
    description: "Configure SMTP settings for your email marketing.",
  },
  "/admin/email-analytics": {
    title: "Email Analytics — Update Email Marketing Tools",
    description:
      "Real-time email analytics with open rates, click rates, and bot-filtered engagement metrics.",
  },
  "/admin/all-message-history": {
    title: "Message History — Update Email Marketing Tools",
    description: "View all sent email messages and their tracking history.",
  },
  "*": {
    title: "Page Not Found — Update Email Marketing Tools",
    description:
      "The page you are looking for could not be found. Return to your dashboard.",
  },
};

/**
 * Match current pathname to a route config.
 * 1. Exact match
 * 2. Prefix match (longest wins)
 * 3. Wildcard fallback
 */
function matchRoute(pathname) {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];

  let bestKey = null;
  for (const key of Object.keys(ROUTE_META)) {
    if (key === "*") continue;
    if (pathname.startsWith(key) && (bestKey === null || key.length > bestKey.length)) {
      bestKey = key;
    }
  }
  return ROUTE_META[bestKey] || ROUTE_META["*"];
}

const PageHelmet = ({ pathname }) => {
  const meta = matchRoute(pathname);
  const url = `${SITE_URL}${pathname}`;
  const fullTitle = meta.title;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={DEFAULT_KEYWORDS} />
      <meta name="author" content={SITE_NAME} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
};

export default PageHelmet;